import classnames from 'classnames';
import React from 'react';
import { connect } from 'react-redux';

import {
  updateShareVideoInUploadMode,
  updateVideoPlayerFlippedCamera,
  updateVideoPlayerPosterUrl,
  updateVideoProcessingError,
  uploadVideoProgress,
} from 'actions/videoPlayer';
import {
  HERMES_ENDPOINT,
  MESSAGE_TYPE_SUBSCRIBE,
  SUBSCRIPTION_TYPE_UPLOAD_PROGRESS,
} from 'constants/hermes';
import { CAM } from 'constants/recordingTypes';
import { LOOM_WS_URI } from 'constants/routes';
import { finishUploadCreator } from 'creators/shareVideo';
import { createImage as preloadImage } from 'utilities/image';
import Websocket from 'utilities/websocket';

import ProgressBar from './progress-bar';

import './styles.less';

let progressValue = 0;

const LOADING_ANIMATION_COMPLETE_TIMEOUT = 1800;

class UploadProgress extends React.Component {
  state = {
    isComplete: false,
  };

  render() {
    const {
      posterUrl,
      inUploadMode,
      videoUploadProgress,
      isFlipped,
    } = this.props;
    const classNames = classnames('upload-progress', {
      active: inUploadMode,
      'is-completed': this.state.isComplete,
    });
    const thumbClassNames = classnames('loading-thumbnail-image', {
      flipped: isFlipped,
    });
    const style = posterUrl && { backgroundImage: `url(${posterUrl})` };

    return (
      <div className={classNames}>
        <div className="loading-thumbnail-container">
          <div className={thumbClassNames} style={style} />
        </div>
        <ProgressBar progress={videoUploadProgress} />
      </div>
    );
  }

  componentDidMount() {
    this.setupUpdateSocket();
  }

  // a fetching action on the video_player is causing this component
  // to constantly re-render.
  // optimize it so that it doesn't happen.
  shouldComponentUpdate(nextProps, nextState) {
    if (
      nextProps.posterUrl === this.props.posterUrl &&
      nextProps.inUploadMode === this.props.inUploadMode &&
      nextProps.videoUploadProgress === this.props.videoUploadProgress &&
      nextState.isComplete === this.state.isComplete
    ) {
      return false;
    }

    return true;
  }

  componentWillUnmount() {
    this.websocket && this.websocket.close();
  }

  onSocketReady = () => {
    const url = window.location.pathname.split('/');
    const videoId = url.pop();

    this.websocket.send(
      JSON.stringify({
        type: MESSAGE_TYPE_SUBSCRIBE,
        subscription_data: {
          type: SUBSCRIPTION_TYPE_UPLOAD_PROGRESS,
          video_id: videoId,
        },
      })
    );
  };

  async onUploadDone() {
    this.setState({ isComplete: true });
    // wait for animation to complete
    await new Promise(resolve =>
      setTimeout(resolve, LOADING_ANIMATION_COMPLETE_TIMEOUT)
    );
    this.exitUploadMode();
    this.websocket.close();
  }

  exitUploadMode() {
    this.props.toggleUploadMode(false);
    this.props.finishUpload();
  }

  onProgress(progress) {
    // make sure progress is between 0 and 100
    // and does not go back
    progressValue = Math.max(progressValue, progress);
    progressValue = Math.min(progressValue, 100);
    this.props.onUploadVideoProgress(progressValue);
  }

  setupUpdateSocket() {
    this.websocket = new Websocket(LOOM_WS_URI + HERMES_ENDPOINT);

    this.websocket.ready(this.onSocketReady);

    this.websocket.onMessage(async ({ data }) => {
      const message = JSON.parse(data);

      if (!message) {
        return;
      }

      if (message.videoUploadValid === false) {
        this.props.updateVideoProcessingError(
          message.videoUploadValid,
          message.videoUploadMessage
        );
      }

      if (message.isFlippedThumb) {
        this.props.updateVideoPlayerFlippedCamera(message.isFlippedThumb);
      }

      if (message.thumbUrl) {
        // preload thumb so we have a smooth experience
        // instead of seeing the image progressively loading
        // on slow connections
        preloadImage(message.thumbUrl)
          .then(img => img.src)
          .then(this.props.updateVideoPlayerPosterUrl);
      }

      // handle case video already uploaded
      // no waiting time, no animation, just exit
      if (message.noUpload === true) {
        this.exitUploadMode();

        return this.websocket.close();
      }

      this.onProgress(message.progress);

      if (message.done === true) {
        return await this.onUploadDone();
      }

      !this.props.inUploadMode && this.props.toggleUploadMode(true);
    });

    this.websocket.onError(err => {
      // exit uploadmode, fallback
      this.exitUploadMode();
      console.error('ws error', err);
    });
  }
}

const mapStateToProps = state => {
  const {
    poster_url: posterUrl,
    in_upload_mode: inUploadMode,
    video_upload_progress: videoUploadProgress,
    flipped_camera: flippedCamera,
    video,
  } = state.video_player;

  const { video_properties: videoProp } = video;
  const { screen_type: screenType } = videoProp || {};
  const isFlipped = screenType === CAM && flippedCamera;

  return { inUploadMode, videoUploadProgress, posterUrl, video, isFlipped };
};

const mapDispatchToProps = dispatch => {
  return {
    onUploadVideoProgress: message => dispatch(uploadVideoProgress(message)),
    toggleUploadMode: uploadMode =>
      dispatch(updateShareVideoInUploadMode(uploadMode)),
    updateVideoPlayerPosterUrl: url =>
      dispatch(updateVideoPlayerPosterUrl(url)),
    finishUpload: () => dispatch(finishUploadCreator()),
    updateVideoPlayerFlippedCamera: flippedCamera =>
      dispatch(updateVideoPlayerFlippedCamera(flippedCamera)),
    updateVideoProcessingError: (videoUploadValid, videoUploadMessage) =>
      dispatch(
        updateVideoProcessingError(videoUploadValid, videoUploadMessage)
      ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadProgress);
